import Phaser from 'phaser';

export interface InputState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  action: boolean;
}

export interface DirectionVector {
  x: number;
  y: number;
}

export class InputSystem {
  private scene: Phaser.Scene;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd?: {
    W: Phaser.Input.Keyboard.Key;
    A: Phaser.Input.Keyboard.Key;
    S: Phaser.Input.Keyboard.Key;
    D: Phaser.Input.Keyboard.Key;
  };
  private actionKey?: Phaser.Input.Keyboard.Key;
  private mobileState: InputState = {
    left: false,
    right: false,
    up: false,
    down: false,
    action: false,
  };
  private mobileAnalog: DirectionVector = { x: 0, y: 0 };
  private actionWasDown = false;
  private actionJustPressedFlag = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    if (scene.input.keyboard) {
      scene.input.keyboard.enabled = true;
      this.cursors = scene.input.keyboard.createCursorKeys();
      this.wasd = scene.input.keyboard.addKeys('W,A,S,D') as {
        W: Phaser.Input.Keyboard.Key;
        A: Phaser.Input.Keyboard.Key;
        S: Phaser.Input.Keyboard.Key;
        D: Phaser.Input.Keyboard.Key;
      };
      this.actionKey = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    }
  }

  setMobileInput(state: Partial<InputState>): void {
    Object.assign(this.mobileState, state);
  }

  setMobileAnalog(x: number, y: number): void {
    this.mobileAnalog.x = Phaser.Math.Clamp(x, -1, 1);
    this.mobileAnalog.y = Phaser.Math.Clamp(y, -1, 1);
  }

  getState(): InputState {
    const keyboard: InputState = {
      left: !!(this.cursors?.left.isDown || this.wasd?.A.isDown),
      right: !!(this.cursors?.right.isDown || this.wasd?.D.isDown),
      up: !!(this.cursors?.up.isDown || this.wasd?.W.isDown),
      down: !!(this.cursors?.down.isDown || this.wasd?.S.isDown),
      action: !!this.actionKey?.isDown,
    };

    return {
      left: keyboard.left || this.mobileState.left,
      right: keyboard.right || this.mobileState.right,
      up: keyboard.up || this.mobileState.up,
      down: keyboard.down || this.mobileState.down,
      action: keyboard.action || this.mobileState.action,
    };
  }

  getDirection(): DirectionVector {
    const state = this.getState();

    if (Math.abs(this.mobileAnalog.x) > 0.15 || Math.abs(this.mobileAnalog.y) > 0.15) {
      const len = Math.sqrt(
        this.mobileAnalog.x * this.mobileAnalog.x + this.mobileAnalog.y * this.mobileAnalog.y,
      );
      if (len > 0) {
        return { x: this.mobileAnalog.x / len, y: this.mobileAnalog.y / len };
      }
    }

    let x = (state.right ? 1 : 0) - (state.left ? 1 : 0);
    let y = (state.down ? 1 : 0) - (state.up ? 1 : 0);
    const len = Math.sqrt(x * x + y * y);
    if (len > 0) return { x: x / len, y: y / len };
    return { x: 0, y: 0 };
  }

  update(): void {
    const actionDown = this.getState().action;
    this.actionJustPressedFlag = actionDown && !this.actionWasDown;
    this.actionWasDown = actionDown;
  }

  isActionJustPressed(): boolean {
    return this.actionJustPressedFlag;
  }

  isActionDown(): boolean {
    return this.getState().action;
  }

  isMobile(): boolean {
    return this.scene.sys.game.device.input.touch;
  }
}
